import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Settings as SettingsIcon,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { MobilePageTitle } from "../components/MobilePageTitle";
import { useBrandProfile } from "../hooks/useBrandProfile";
import { MiMarcaProductsTab } from "../components/MiMarcaProductsTab";
import { TiendaNubeStatus } from "../components/TiendaNubeStatus";
import { MiMarcaModelsTab } from "../components/MiMarcaModelsTab";
import { CompanyData } from "./config_sections/CompanyData";

type TabKey = "productos" | "diseno" | "modelos";
const TABS: { key: TabKey; label: string }[] = [
  { key: "productos", label: "Mis productos" },
  { key: "diseno", label: "Mi diseño" },
  { key: "modelos", label: "Mis Modelos" },
];

/**
 * Pantalla "Mi marca": un selector de tabs (Mis productos · Mi diseño · Mis
 * Modelos) con el contenido del tab elegido al costado.
 *
 * Antes abría con una card de marca (logo + usuario + marca + plan, que además
 * era el acceso a "Cambiar de marca") y un bloque de créditos restantes. Los dos
 * se quitaron —ADR-2026-07-31-rubro-selector-sale-marca-real y ADR-2026-07-31-mi-marca-card-marca-pantalla—: eran identidad y saldo, que el shell ya muestra
 * en la sidebar y en la barra de créditos desde cualquier pantalla.
 */
export const MiMarca = () => {
  const navigate = useNavigate();
  const { profile, currentBrandId } = useBrandProfile();

  const [tab, setTab] = useState<TabKey>("productos");
  // ── Deep-link `?conectar=tiendanube` (banner "Conectá tu Tienda Nube" de la
  //    home) → abre el modal de conexión solo, sin que el user tenga que buscar
  //    el botón.
  //
  //    El estado vive ACÁ y no en `TiendaNubeStatus` porque hay DOS instancias
  //    montadas a la vez (ícono mobile + card desktop, ocultas por CSS). Se le
  //    pasa a UNA sola (la card): su modal usa `createPortal` a document.body,
  //    así que se ve bien igual aunque en mobile su contenedor esté oculto.
  //    Si se le pasara a las dos, se abrirían dos modales superpuestos.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tnConnectOpen, setTnConnectOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("conectar") !== "tiendanube") return;
    setTnConnectOpen(true);
    // Limpiar el param para que el modal no se re-abra al volver atrás o al
    // refrescar. `replace` para no ensuciar el historial.
    const next = new URLSearchParams(searchParams);
    next.delete("conectar");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // Error/success para el componente CompanyData (Mi diseño).
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  return (
    <div className="relative px-4 sm:px-8 pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen">
      {/* Acciones del header (solo mobile): ícono de Tiendanube + Configuración.
          En desktop la config va por el sidebar y Tiendanube por la card de la
          columna izquierda. */}
      <div className="md:hidden absolute top-1 right-4 sm:right-8 flex items-center gap-0.5">
        <TiendaNubeStatus
          variant="icon"
          companyId={profile?.id_company ?? null}
          brandId={currentBrandId}
        />
        <button
          type="button"
          onClick={() => navigate("/configuracion")}
          aria-label="Configuración"
          className="inline-flex items-center justify-center w-10 h-10 rounded-full text-brand-black/70 hover:bg-black/[0.04] active:opacity-60 transition-colors"
        >
          <SettingsIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Título mobile (desktop usa el header de abajo) */}
      <MobilePageTitle>Mi marca</MobilePageTitle>
      {/* Desktop header. En lg queda sticky arriba (con fondo) para seguir
          visible al scrollear el contenido. */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11 lg:mb-0 lg:h-auto lg:py-4 lg:sticky lg:top-0 lg:z-20 lg:bg-surface-content">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4 lg:mt-0">
          Mi marca
        </h1>
      </div>

      {/* Layout: 1 columna hasta lg; en lg+ dos columnas (izq: marca +
          créditos + tabs verticales / der: contenido del tab). Usamos lg y no
          md porque a md el sidebar + columna izquierda dejan el área derecha
          demasiado angosta. */}
      <div className="lg:grid lg:grid-cols-[440px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        {/* ── Columna izquierda (fija al scrollear en desktop, debajo del
            título sticky) ── */}
        <div className="lg:sticky lg:top-[60px] lg:self-start">
          {/* Acá iban, uno debajo del otro, la card de marca (logo + usuario +
              marca + plan, que además abría el switcher) y el bloque "Créditos
              restantes" con su barra. Los dos se quitaron — ADR-2026-07-31-rubro-selector-sale-marca-real y ADR-2026-07-31-mi-marca-card-marca-pantalla.

              La columna arranca directamente en los tabs: esta pantalla es para
              editar la marca, y lo que se sacó era identidad y saldo, que se ven
              en el shell (sidebar + barra de créditos) desde cualquier pantalla.

              ⚠️ Con la card se fue el ÚNICO acceso a "Cambiar de marca" que tenía
              esta pantalla. El de `Sidebar.tsx` sigue existiendo, pero el shell
              nuevo no tiene ninguno todavía: su selector de marca navega acá en
              vez de desplegar la lista. Ver ADR-2026-07-31-mi-marca-card-marca-pantalla. */}

          {/* Tabs horizontales (mobile / hasta lg) */}
          <div className="lg:hidden flex border-b border-black/[0.08]">
            {TABS.map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex-1 pb-2.5 text-[13.5px] font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? "border-brand-primary text-brand-primary"
                      : "border-transparent text-brand-black/50 hover:text-brand-black/80"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tabs como containers verticales (lg+) */}
          <div className="hidden lg:flex lg:flex-col gap-2">
            {TABS.map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-[14px] font-medium transition-colors ${
                    isActive
                      ? "border-brand-primary/40 bg-brand-primary/[0.06] text-brand-primary"
                      : "border-black/[0.07] bg-white hover:bg-black/[0.02] text-brand-black/80"
                  }`}
                >
                  {t.label}
                  <ChevronRight
                    className={`w-4 h-4 transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`}
                  />
                </button>
              );
            })}
          </div>

          {/* Integración Tiendanube — desde md+ (tablet/desktop) va acá, separada
              de los tabs con una divisoria + rótulo. En mobile va como ícono en
              el header (arriba a la derecha). */}
          <div className="hidden md:block mt-6 pt-5 border-t border-black/[0.08]">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-black/35 mb-2 px-1">
              Integración
            </p>
            {/* Única instancia con el modal controlado — ver el comentario del
                deep-link arriba. La instancia mobile (ícono) sigue con su
                estado interno. */}
            <TiendaNubeStatus
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
              connectOpen={tnConnectOpen}
              onConnectOpenChange={setTnConnectOpen}
            />
          </div>
        </div>

        {/* ── Columna derecha: contenido del tab seleccionado ── */}
        <div className="mt-5 lg:mt-0">
          {/* Banners de error/success (para Mi diseño) */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[14px] text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[14px] text-green-700">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}

          {tab === "productos" && (
            <MiMarcaProductsTab
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
            />
          )}
          {tab === "diseno" &&
            (profile?.id_company ? (
              <CompanyData
                profile={profile}
                setError={setError}
                setSuccess={setSuccess}
                embedded
              />
            ) : (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-brand-black/30" />
              </div>
            ))}
          {tab === "modelos" && (
            <MiMarcaModelsTab companyId={profile?.id_company ?? null} />
          )}
        </div>
      </div>

      {/* El modal de "Cambiar de marca" se fue con la card que lo abría (ADR-2026-07-31-mi-marca-card-marca-pantalla):
          sin ese botón quedaba inalcanzable. Si hace falta reponerlo, la
          implementación equivalente y viva está en `Sidebar.tsx`. */}
    </div>
  );
};
